import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { PersonnelPickerDropdown } from './PersonnelPickerDropdown';
import { usePageMeta } from '../hooks/usePageMeta';
import './SalesOrgHierarchyPage.css';

const API_BASE = '/api/sales-org-hierarchy';
const PERSONNEL_API = '/api/personnel';
const MAX_LEVELS = 7;

// Seviye derinliğine göre renk paleti (kök → yaprak).
const LEVEL_COLORS = ['#0073ea', '#0ea5e9', '#14b8a6', '#00c875', '#fdab3d', '#a855f7', '#e2445c'];
const levelColor = (lvl) => LEVEL_COLORS[(lvl - 1) % LEVEL_COLORS.length];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'Data', 'data') ?? payload; }

// Personel adından baş harf(ler) — avatar dairesi için. "Ahmet Yılmaz" → "AY".
function initialsOf(label) {
  const parts = String(label || '').replace(/\(.*\)/, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '–';
  if (parts.length === 1) return parts[0].slice(0, 2).toLocaleUpperCase('tr-TR');
  return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase('tr-TR');
}
// "Ad Soyad (SICIL)" → "Ad Soyad"
function nameOnly(label) { return String(label || '').replace(/\s*\(.*\)\s*$/, '').trim(); }

const st = {
  page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  card: {
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 16, padding: 20, boxShadow: '0 10px 24px -14px rgba(15,23,42,0.18)',
  },
  cardTitle: { margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: 'var(--text-primary, #111827)' },
  cardSub: { margin: '0 0 16px', fontSize: 13, color: 'var(--text-secondary, #6b7280)' },
  levelPills: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 },
  levelPill: (active) => ({
    width: 38, height: 38, borderRadius: 12, cursor: 'pointer', fontWeight: 800, fontSize: 15,
    border: active ? '1px solid var(--accent)' : '1px solid var(--border-color, #d1d5db)',
    background: active ? 'var(--accent)' : 'var(--input-bg, #fff)',
    color: active ? '#fff' : 'var(--text-primary, #1f2937)',
  }),
  levelNameGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  treeEmpty: { color: 'var(--text-secondary, #6b7280)', fontSize: 14, padding: '8px 0' },
  treeToolbar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  // "Personel yok" yönlendirme kartı (boş ağaç durumunda).
  guideCard: {
    border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--input-bg, #f9fafb)',
    borderRadius: 12, padding: '14px 16px', maxWidth: 560,
  },
  // Dal modalı içi "bu seviyede personel yok" uyarı + hızlı geçiş şeridi.
  modalNotice: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    border: '1px solid #fcd34d', background: 'rgba(251,191,36,0.12)', color: '#92400e',
    borderRadius: 10, padding: '10px 12px', marginBottom: 14, fontSize: 13,
  },
};

export function SalesOrgHierarchyPage() {
  usePageMeta('Satış Organizasyon Hiyerarşisi', 'Seviye yapısını tanımlayın ve organizasyon ağacını oluşturun.');
  const navigate = useNavigate();

  const [levelCount, setLevelCount] = useState(3);
  const [levelNames, setLevelNames] = useState(['', '', '']);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [toast, setToast] = useState(null);

  // Görünüm + ölçeklenebilirlik durumu
  const [viewMode, setViewMode] = useState('chart'); // 'chart' | 'list'
  const [collapsed, setCollapsed] = useState(() => new Set()); // kapalı düğüm id'leri
  const [treeQuery, setTreeQuery] = useState('');

  // Dal modalı: { mode:'add'|'edit', parentId, levelNo, nodeId?, primaryId, primaryLabel, coId, coLabel }
  const [nodeModal, setNodeModal] = useState(null);
  const [savingNode, setSavingNode] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Hiyerarşi alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      const lc = pick(data, 'levelCount', 'LevelCount') ?? 3;
      const names = pick(data, 'levelNames', 'LevelNames') ?? [];
      setLevelCount(lc);
      setLevelNames(Array.from({ length: lc }, (_, i) => names[i] ?? ''));
      const ns = pick(data, 'nodes', 'Nodes') ?? [];
      setNodes(ns.map((n) => ({
        id: pick(n, 'id', 'Id'),
        parentId: pick(n, 'parentId', 'ParentId') ?? null,
        levelNo: pick(n, 'levelNo', 'LevelNo'),
        personnelId: pick(n, 'personnelId', 'PersonnelId'),
        personnelLabel: pick(n, 'personnelLabel', 'PersonnelLabel') ?? '',
        // #44 Oturum hesabı bağı olmayan personel ekip mesajlaşmasına dahil olamaz.
        personnelHasLogin: pick(n, 'personnelHasLogin', 'PersonnelHasLogin') ?? false,
        coPersonnelId: pick(n, 'coPersonnelId', 'CoPersonnelId') ?? null,
        coPersonnelLabel: pick(n, 'coPersonnelLabel', 'CoPersonnelLabel') ?? '',
        coPersonnelHasLogin: pick(n, 'coPersonnelHasLogin', 'CoPersonnelHasLogin') ?? false,
        sortOrder: pick(n, 'sortOrder', 'SortOrder') ?? 0,
      })));
    } catch (err) {
      showToast('error', err.message || 'Hiyerarşi alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Seviye başına tanımlı AKTİF personel sayısı. Bir dal yalnızca kendi seviyesine atanmış
  // personelle doldurulabildiği için, personel yoksa kullanıcıyı önce Personel Tanımlama'ya
  // yönlendiririz (yoksa "Direktör eklenemiyor / 400" çıkmazına düşülüyor).
  const [levelPersonnelCount, setLevelPersonnelCount] = useState({});
  const fetchLevelPersonnel = useCallback(async () => {
    try {
      const res = await fetch(`${PERSONNEL_API}?isActive=true&take=500`, { headers: authHeaders() });
      if (!res.ok) return;
      const items = unwrap(await res.json());
      const counts = {};
      (Array.isArray(items) ? items : []).forEach((p) => {
        const l = pick(p, 'levelNo', 'LevelNo');
        if (l != null) counts[l] = (counts[l] || 0) + 1;
      });
      setLevelPersonnelCount(counts);
    } catch { /* sessiz — yönlendirme kartı en kötü durumda gösterilmez */ }
  }, []);
  useEffect(() => { fetchLevelPersonnel(); }, [fetchLevelPersonnel]);

  const hasPersonnelAt = useCallback((no) => (levelPersonnelCount[no] || 0) > 0, [levelPersonnelCount]);

  // "Kolay geçiş": ilgili seviye önceden seçili olarak Personel Tanımlama (yeni) ekranını açar.
  const goCreatePersonnel = (no) => {
    navigate('/personnel/new', {
      state: {
        clone: {
          sicilId: '', firstName: '', lastName: '', hireDate: '', workStatus: 'ACTIVE',
          position: '', phone: '', address: '', email: '', levelNo: no || 1, isActive: 'true',
        },
      },
    });
  };

  const levelName = useCallback((no) => levelNames[no - 1] || `Seviye ${no}`, [levelNames]);

  // ── Ağaç indeksleri ──
  const childrenMap = useMemo(() => {
    const m = new Map();
    nodes.forEach((n) => {
      const arr = m.get(n.parentId) || [];
      arr.push(n);
      m.set(n.parentId, arr);
    });
    m.forEach((arr) => arr.sort((a, b) => a.sortOrder - b.sortOrder));
    return m;
  }, [nodes]);
  const childrenOf = useCallback((id) => childrenMap.get(id) || [], [childrenMap]);
  const rootNode = useMemo(() => nodes.find((n) => n.parentId == null) || null, [nodes]);

  // Bir dalın toplam alt-eleman (kişi) sayısı — kapalı rozet için.
  const descendantCount = useCallback((id) => {
    let total = 0;
    const walk = (pid) => childrenOf(pid).forEach((c) => { total += c.coPersonnelId ? 2 : 1; walk(c.id); });
    walk(id);
    return total;
  }, [childrenOf]);

  // İlk yüklemede 2. seviyeden derinleri otomatik daralt (büyük org için).
  useEffect(() => {
    if (nodes.length === 0) return;
    const deep = new Set();
    nodes.forEach((n) => { if (n.levelNo >= 2 && childrenOf(n.id).length > 0) deep.add(n.id); });
    setCollapsed(deep);
  }, [nodes, childrenOf]);

  // ── Arama: eşleşen düğümler + tüm ataları görünür; aramada daraltma yok sayılır. ──
  const q = treeQuery.trim().toLocaleLowerCase('tr-TR');
  const searchInfo = useMemo(() => {
    if (!q) return null;
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const matched = new Set();
    nodes.forEach((n) => {
      const hay = `${n.personnelLabel} ${n.coPersonnelLabel}`.toLocaleLowerCase('tr-TR');
      if (hay.includes(q)) matched.add(n.id);
    });
    const visible = new Set();
    matched.forEach((id) => {
      let cur = byId.get(id);
      while (cur) { visible.add(cur.id); cur = cur.parentId ? byId.get(cur.parentId) : null; }
    });
    return { matched, visible };
  }, [q, nodes]);

  const isCollapsed = (id) => !searchInfo && collapsed.has(id);
  const toggleCollapse = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => {
    const all = new Set();
    nodes.forEach((n) => { if (childrenOf(n.id).length > 0 && n.parentId != null) all.add(n.id); });
    setCollapsed(all);
  };

  const changeLevelCount = (n) => {
    setLevelCount(n);
    setLevelNames((prev) => Array.from({ length: n }, (_, i) => prev[i] ?? ''));
  };

  const saveConfig = async () => {
    if (levelNames.some((nm) => !nm.trim())) { showToast('error', 'Tüm seviye adlarını doldurun.'); return; }
    setSavingConfig(true);
    try {
      const res = await fetch(`${API_BASE}/config`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ levelCount, levelNames: levelNames.map((nm) => nm.trim()) }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Kayıt başarısız (${res.status})`);
      showToast('success', 'Seviye yapılandırması kaydedildi.');
      fetchAll();
    } catch (err) {
      showToast('error', err.message || 'Kayıt başarısız');
    } finally {
      setSavingConfig(false);
    }
  };

  // ── Dal ekle / düzenle modalı ──
  const openAdd = (parentId, levelNo) => setNodeModal({ mode: 'add', parentId, levelNo, primaryId: '', primaryLabel: '', coId: '', coLabel: '' });
  const openEdit = (node) => setNodeModal({
    mode: 'edit', nodeId: node.id, levelNo: node.levelNo,
    primaryId: node.personnelId, primaryLabel: nameOnly(node.personnelLabel),
    coId: node.coPersonnelId || '', coLabel: nameOnly(node.coPersonnelLabel),
  });
  const closeNodeModal = () => setNodeModal(null);

  const saveNode = async () => {
    if (!nodeModal.primaryId) { showToast('error', 'Bir personel seçin.'); return; }
    if (nodeModal.coId && nodeModal.coId === nodeModal.primaryId) { showToast('error', 'Aynı personel iki kez seçilemez.'); return; }
    setSavingNode(true);
    try {
      const isAdd = nodeModal.mode === 'add';
      const url = isAdd ? `${API_BASE}/nodes` : `${API_BASE}/nodes/${nodeModal.nodeId}`;
      const body = isAdd
        ? { parentId: nodeModal.parentId, personnelId: nodeModal.primaryId, coPersonnelId: nodeModal.coId || null }
        : { personnelId: nodeModal.primaryId, coPersonnelId: nodeModal.coId || null };
      const res = await fetch(url, { method: isAdd ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(body) });
      const resp = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(resp, 'Message', 'message') || `İşlem başarısız (${res.status})`);
      showToast('success', isAdd ? 'Dal eklendi.' : 'Dal güncellendi.');
      setNodeModal(null);
      fetchAll();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
      // Sunucu reddetti (örn. "Kök dal zaten mevcut"): ağaç bayat kalmış olabilir; tazele ki
      // gerçek durum (varsa mevcut kök) ekrana yansısın ve kullanıcı tıkanmasın.
      fetchAll();
    } finally {
      setSavingNode(false);
    }
  };

  const deleteNode = async (node) => {
    if (!window.confirm('Bu dal ve tüm alt dalları silinecek. Emin misiniz?')) return;
    try {
      const res = await fetch(`${API_BASE}/nodes/${node.id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Dal silindi.');
      fetchAll();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    }
  };

  // ── Node içeriği (her iki görünümde ortak) ──
  const NodeBody = ({ node }) => {
    const color = levelColor(node.levelNo);
    const matched = searchInfo?.matched.has(node.id);
    const hasCo = !!node.coPersonnelId;
    return (
      <div className={`org-node${matched ? ' is-match' : ''}`} style={{ '--org-accent': color }}>
        <div className="org-node-head">
          <span className="org-node-avatars">
            <span className="org-node-avatar" style={{ background: color }}>{initialsOf(node.personnelLabel)}</span>
            {hasCo && <span className="org-node-avatar org-node-avatar--co" style={{ background: color }}>{initialsOf(node.coPersonnelLabel)}</span>}
          </span>
          <div className="org-node-info">
            <span className="org-node-name" title={nameOnly(node.personnelLabel) + (hasCo ? ' & ' + nameOnly(node.coPersonnelLabel) : '')}>
              {nameOnly(node.personnelLabel) || 'Atanmamış'}
              {hasCo && <span className="org-node-amp"> &amp; </span>}
              {hasCo && nameOnly(node.coPersonnelLabel)}
            </span>
            <span className="org-node-level">
              {levelName(node.levelNo)}{hasCo && <span className="org-node-shared" title="Pozisyon paylaşımlı">· paylaşımlı</span>}
              {/* #44 Oturum hesabı bağı eksikse mesajlaşma bu kişi için çalışmaz — kurulum
                  ekranında görünür olsun ki "neden yazamıyorum" sorusu burada cevaplansın. */}
              {(!node.personnelHasLogin || (hasCo && !node.coPersonnelHasLogin)) && (
                <span
                  className="org-node-shared"
                  title={!node.personnelHasLogin && hasCo && !node.coPersonnelHasLogin
                    ? 'İki personelin de oturum hesabı bağı yok — mesajlaşmaya dahil değiller'
                    : 'Oturum hesabı bağı yok — mesajlaşmaya dahil değil (Personel kartından bağlayın)'}
                >
                  · hesap yok
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="org-node-actions">
          {node.levelNo < levelCount && (
            <button type="button" className="org-node-btn org-node-btn--add" title={`${levelName(node.levelNo + 1)} ekle`} onClick={() => openAdd(node.id, node.levelNo + 1)}>
              <span aria-hidden="true">+</span> Dal
            </button>
          )}
          <button type="button" className="org-node-btn org-node-btn--edit" title="Düzenle" onClick={() => openEdit(node)}>Düzenle</button>
          <button type="button" className="org-node-btn org-node-btn--del" title="Dalı sil" onClick={() => deleteNode(node)}>Sil</button>
        </div>
      </div>
    );
  };

  // Daralt/genişlet düğmesi (sayı rozetiyle).
  const Toggle = ({ node }) => {
    const kids = childrenOf(node.id);
    if (kids.length === 0) return <span className="org-toggle org-toggle--leaf" aria-hidden="true" />;
    const c = isCollapsed(node.id);
    return (
      <button type="button" className="org-toggle" onClick={() => toggleCollapse(node.id)} title={c ? 'Genişlet' : 'Daralt'}>
        <span className="org-toggle-caret">{c ? '▸' : '▾'}</span>
        {c && <span className="org-toggle-count">{descendantCount(node.id)}</span>}
      </button>
    );
  };

  // ── ŞEMA görünümü (yatay org-chart) ──
  const renderChartNode = (node) => {
    if (searchInfo && !searchInfo.visible.has(node.id)) return null;
    const kids = childrenOf(node.id);
    const showKids = kids.length > 0 && !isCollapsed(node.id);
    return (
      <li key={node.id}>
        <div className="org-chart-cell">
          <NodeBody node={node} />
          <Toggle node={node} />
        </div>
        {showKids && <ul>{kids.map(renderChartNode)}</ul>}
      </li>
    );
  };

  // ── LİSTE görünümü (girintili tree-view) ──
  const renderListNode = (node, depth) => {
    if (searchInfo && !searchInfo.visible.has(node.id)) return null;
    const kids = childrenOf(node.id);
    const showKids = kids.length > 0 && !isCollapsed(node.id);
    return (
      <div key={node.id} className="org-list-branch">
        <div className="org-list-row" style={{ paddingLeft: depth * 26 }}>
          <span className="org-list-guide" style={{ width: depth * 26 }} aria-hidden="true" />
          <Toggle node={node} />
          <NodeBody node={node} />
        </div>
        {showKids && kids.map((k) => renderListNode(k, depth + 1))}
      </div>
    );
  };

  const totalPeople = useMemo(() => nodes.reduce((acc, n) => acc + (n.coPersonnelId ? 2 : 1), 0), [nodes]);

  return (
    <div style={st.page}>
      {/* Seviye yapılandırması */}
      <div style={st.card}>
        <h3 style={st.cardTitle}>Seviye Yapısı</h3>
        <p style={st.cardSub}>Hiyerarşi seviye sayısını seçin (en fazla {MAX_LEVELS}) ve her seviyenin adını girin.</p>

        <div style={st.levelPills}>
          {Array.from({ length: MAX_LEVELS }, (_, i) => i + 1).map((n) => (
            <button key={n} style={st.levelPill(n === levelCount)} onClick={() => changeLevelCount(n)}>{n}</button>
          ))}
        </div>

        <div style={st.levelNameGrid}>
          {Array.from({ length: levelCount }, (_, i) => (
            <div key={i} style={s.formGroup}>
              <label style={s.label}>Seviye {i + 1}</label>
              <input
                style={s.input}
                value={levelNames[i] ?? ''}
                placeholder={`Örn. ${['Direktör', 'Bölge Şefi', 'Satış Temsilcisi'][i] || 'Seviye adı'}`}
                onChange={(e) => setLevelNames((prev) => { const c = [...prev]; c[i] = e.target.value; return c; })}
              />
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
          <button style={{ ...s.btnPrimary, opacity: savingConfig ? 0.6 : 1 }} onClick={saveConfig} disabled={savingConfig}>
            {savingConfig ? 'Kaydediliyor...' : 'Yapılandırmayı Kaydet'}
          </button>
          <button style={s.btnSecondary} onClick={fetchAll}>Yenile</button>
        </div>
      </div>

      {/* Hiyerarşi ağacı */}
      <div style={st.card}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={st.cardTitle}>Hiyerarşi Ağacı</h3>
          <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>{nodes.length} dal · {totalPeople} kişi</span>
        </div>
        <p style={st.cardSub}>Her dala, ilgili seviyeye atanmış personel eklenir. Bir pozisyon iki kişi tarafından paylaşılabilir.</p>

        {/* Ağaç araç çubuğu: görünüm + arama + genişlet/daralt */}
        <div style={st.treeToolbar}>
          <div className="org-viewswitch">
            <button type="button" className={`org-viewswitch-btn${viewMode === 'chart' ? ' is-active' : ''}`} onClick={() => setViewMode('chart')}>Şema</button>
            <button type="button" className={`org-viewswitch-btn${viewMode === 'list' ? ' is-active' : ''}`} onClick={() => setViewMode('list')}>Liste</button>
          </div>
          <div className="org-tree-search">
            <input
              type="text"
              value={treeQuery}
              onChange={(e) => setTreeQuery(e.target.value)}
              placeholder="Ağaçta personel ara…"
              aria-label="Ağaçta ara"
            />
            {treeQuery && <button type="button" className="org-tree-search-clear" onClick={() => setTreeQuery('')} aria-label="Temizle">×</button>}
          </div>
          <button style={s.btnSecondary} onClick={expandAll} disabled={!!searchInfo}>Tümünü Genişlet</button>
          <button style={s.btnSecondary} onClick={collapseAll} disabled={!!searchInfo}>Tümünü Daralt</button>
        </div>

        {loading ? (
          <div style={st.treeEmpty}>Yükleniyor...</div>
        ) : !rootNode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <div style={st.treeEmpty}>Henüz kök dal yok. Ağaç en üst seviye ({levelName(1)}) ile başlar.</div>
            {hasPersonnelAt(1) ? (
              <button style={s.btnPrimary} onClick={() => openAdd(null, 1)}>+ Kök Dal Ekle ({levelName(1)})</button>
            ) : (
              <div style={st.guideCard}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Önce {levelName(1)} personeli tanımlayın</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', marginBottom: 10, lineHeight: 1.6 }}>
                  Ağaca dal eklemek için, o seviyeye atanmış aktif personel gerekir. Şu an <b>{levelName(1)}</b> seviyesinde
                  tanımlı personel yok; bu yüzden direktör eklenemiyor. Önce personeli tanımlayın, sonra buraya dönüp ağacı kurun.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={s.btnPrimary} onClick={() => goCreatePersonnel(1)}>+ {levelName(1)} Personeli Ekle</button>
                  <button style={s.btnSecondary} onClick={() => navigate('/personnel')}>Personel Tanımlama</button>
                </div>
              </div>
            )}
          </div>
        ) : searchInfo && searchInfo.visible.size === 0 ? (
          <div style={st.treeEmpty}>Eşleşen personel yok.</div>
        ) : viewMode === 'chart' ? (
          <div className="org-tree-scroll">
            <ul className="org-tree">{renderChartNode(rootNode)}</ul>
          </div>
        ) : (
          <div className="org-list">{renderListNode(rootNode, 0)}</div>
        )}
      </div>

      {/* Dal ekleme / düzenleme modalı */}
      {nodeModal && (
        <div style={s.overlay} onClick={closeNodeModal}>
          <div style={{ ...s.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {nodeModal.mode === 'add' ? 'Dal Ekle' : 'Dalı Düzenle'} — {levelName(nodeModal.levelNo)}
              </h3>
              <button style={s.closeBtn} onClick={closeNodeModal}>×</button>
            </div>
            <div style={s.modalBody}>
              {!hasPersonnelAt(nodeModal.levelNo) && (
                <div style={st.modalNotice}>
                  <span>
                    <b>{levelName(nodeModal.levelNo)}</b> seviyesine atanmış aktif personel yok.
                    Önce bu seviyeye personel tanımlayın.
                  </span>
                  <button style={s.btnSecondary} onClick={() => goCreatePersonnel(nodeModal.levelNo)}>
                    + Personel Tanımla
                  </button>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Personel ({levelName(nodeModal.levelNo)} seviyesi)</label>
                <PersonnelPickerDropdown
                  value={nodeModal.primaryId}
                  selectedLabel={nodeModal.primaryLabel}
                  onChange={(v, item) => setNodeModal((m) => ({ ...m, primaryId: v, primaryLabel: item?.label || '' }))}
                  levelNo={nodeModal.levelNo}
                  placeholder="Personel seç…"
                  emptyHint={`${levelName(nodeModal.levelNo)} seviyesine atanmış aktif personel yok. Önce Personel Tanımlama'dan ekleyin.`}
                  showToast={showToast}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Pozisyonu paylaşan 2. personel <span style={{ fontWeight: 400, color: 'var(--text-secondary,#6b7280)' }}>(opsiyonel)</span></label>
                <PersonnelPickerDropdown
                  value={nodeModal.coId}
                  selectedLabel={nodeModal.coLabel}
                  onChange={(v, item) => setNodeModal((m) => ({ ...m, coId: v, coLabel: item?.label || '' }))}
                  levelNo={nodeModal.levelNo}
                  placeholder="(Yok) — paylaşan personel seç…"
                  emptyHint={`${levelName(nodeModal.levelNo)} seviyesinde başka personel yok.`}
                  showToast={showToast}
                />
                <span style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                  Bir pozisyonu iki kişi paylaşıyorsa ikinci personeli buradan ekleyin (aynı seviyeden).
                </span>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnPrimary, opacity: savingNode ? 0.6 : 1 }} onClick={saveNode} disabled={savingNode}>
                {savingNode ? 'Kaydediliyor...' : (nodeModal.mode === 'add' ? 'Ekle' : 'Kaydet')}
              </button>
              <button style={s.btnSecondary} onClick={closeNodeModal} disabled={savingNode}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default SalesOrgHierarchyPage;
